import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CANVAS_W = 800;
const CANVAS_H = 500;

export default function AirCombatPlayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gameType = searchParams.get('type') || 'defence';
  const isAttack = gameType === 'attack';

  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const keysRef = useRef({});
  const [gameOver, setGameOver] = useState(false);
  const scoreRef = useRef(0);
  const livesRef = useRef(20);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(20);

  const initGame = useCallback(() => {
    const startX = isAttack ? 60 : CANVAS_W - 110;
    const startY = CANVAS_H / 2 - 12;
    return {
      player: { x: startX, y: startY, w: 50, h: 24, speed: 5 },
      bullets: [],
      enemies: [],
      enemyBullets: [],
      explosions: [],
      clouds: Array.from({ length: 5 }, () => ({
        x: Math.random() * CANVAS_W,
        y: 20 + Math.random() * 120,
        w: 50 + Math.random() * 70,
        speed: 0.2 + Math.random() * 0.4,
      })),
      frame: 0,
      kills: 0,
      mapScroll: 0,
    };
  }, [isAttack]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    gameRef.current = initGame();
    scoreRef.current = 0;
    livesRef.current = 20;
    setScore(0);
    setLives(20);
    setGameOver(false);

    const handleKey = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
        e.preventDefault();
      }
      keysRef.current[e.key] = e.type === 'keydown';
    };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', handleKey);

    let animId;
    const loop = () => {
      if (!gameRef.current) return;
      update(ctx);
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keyup', handleKey);
    };
  }, [initGame]); // mount-only

  const collides = (a, b) =>
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

  const endGame = useCallback((won) => {
    setGameOver(true);
    navigate(`/air-combat/results?score=${scoreRef.current}&type=${gameType}&kills=${gameRef.current?.kills || 0}&won=${won}`);
  }, [navigate, gameType]);

  const update = (ctx) => {
    const g = gameRef.current;
    if (!g || gameOver) return;
    g.frame++;

    const keys = keysRef.current;
    const p = g.player;

    // Player movement
    if (keys['ArrowUp'] && p.y > 40) p.y -= p.speed;
    if (keys['ArrowDown'] && p.y < CANVAS_H - 50 - p.h) p.y += p.speed;
    if (keys['ArrowLeft'] && p.x > 0) p.x -= p.speed;
    if (keys['ArrowRight'] && p.x < CANVAS_W - p.w) p.x += p.speed;

    // Shoot
    const bulletDir = isAttack ? 1 : -1;
    if (keys[' '] && g.frame % 10 === 0) {
      const bx = isAttack ? p.x + p.w : p.x - 12;
      g.bullets.push({ x: bx, y: p.y + p.h / 2 - 2, w: 12, h: 4, speed: 8 * bulletDir });
    }

    // Move bullets
    g.bullets = g.bullets.filter((b) => {
      b.x += b.speed;
      return b.x > -20 && b.x < CANVAS_W + 20;
    });

    // Spawn enemies
    const spawnRate = 70;
    if (g.frame % spawnRate === 0) {
      const ey = 50 + Math.random() * (CANVAS_H - 120);
      if (isAttack) {
        // Attack: enemies come from right (Pakistan side)
        g.enemies.push({ x: CANVAS_W, y: ey, w: 44, h: 20, speed: 2 + Math.random(), dir: -1 });
      } else {
        // Defence: enemies come from left (attacking Delhi)
        g.enemies.push({ x: -44, y: ey, w: 44, h: 20, speed: 2 + Math.random(), dir: 1 });
      }
    }

    // Move enemies + shoot
    g.enemies = g.enemies.filter((e) => {
      e.x += e.speed * e.dir;
      if (g.frame % 100 === 0 && Math.random() > 0.4) {
        const ebDir = isAttack ? -5 : 5;
        g.enemyBullets.push({ x: e.x + (e.dir > 0 ? e.w : 0), y: e.y + e.h / 2 - 2, w: 10, h: 3, speed: ebDir });
      }
      return e.x > -60 && e.x < CANVAS_W + 60;
    });

    // Move enemy bullets
    g.enemyBullets = g.enemyBullets.filter((b) => {
      b.x += b.speed;
      return b.x > -20 && b.x < CANVAS_W + 20;
    });

    // Collision: player bullets vs enemies
    for (let bi = g.bullets.length - 1; bi >= 0; bi--) {
      const b = g.bullets[bi];
      for (let ei = g.enemies.length - 1; ei >= 0; ei--) {
        if (collides(b, g.enemies[ei])) {
          g.explosions.push({ x: g.enemies[ei].x, y: g.enemies[ei].y, frame: 0, max: 20 });
          g.enemies.splice(ei, 1);
          g.bullets.splice(bi, 1);
          g.kills++;
          scoreRef.current += 10;
          setScore(scoreRef.current);
          break;
        }
      }
    }

    // Collision: enemy bullets/planes vs player
    for (let bi = g.enemyBullets.length - 1; bi >= 0; bi--) {
      if (collides(g.enemyBullets[bi], p)) {
        g.enemyBullets.splice(bi, 1);
        livesRef.current--;
        setLives(livesRef.current);
        g.explosions.push({ x: p.x, y: p.y, frame: 0, max: 15 });
        if (livesRef.current <= 0) { endGame(false); return; }
      }
    }
    for (let ei = g.enemies.length - 1; ei >= 0; ei--) {
      if (collides(g.enemies[ei], p)) {
        g.explosions.push({ x: g.enemies[ei].x, y: g.enemies[ei].y, frame: 0, max: 20 });
        g.enemies.splice(ei, 1);
        livesRef.current--;
        setLives(livesRef.current);
        if (livesRef.current <= 0) { endGame(false); return; }
      }
    }

    // Win at 30 kills
    if (g.kills >= 30) { endGame(true); return; }

    // Explosions
    g.explosions = g.explosions.filter((ex) => { ex.frame++; return ex.frame < ex.max; });

    // Clouds
    g.clouds.forEach((c) => {
      c.x -= c.speed;
      if (c.x < -c.w) { c.x = CANVAS_W + c.w; c.y = 20 + Math.random() * 120; }
    });

    g.mapScroll = (g.mapScroll + 0.5) % CANVAS_W;

    draw(ctx, g);
  };

  const draw = (ctx, g) => {
    // Sky
    const grad = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
    grad.addColorStop(0, '#1a365d');
    grad.addColorStop(0.3, '#2c5282');
    grad.addColorStop(0.7, '#63b3ed');
    grad.addColorStop(1, '#48bb78');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Ground with country colors
    const groundY = CANVAS_H - 40;
    ctx.fillStyle = '#276749';
    ctx.fillRect(0, groundY, CANVAS_W, 40);

    // India side (green) and Pakistan side (grey)
    if (isAttack) {
      // Player on left (India), enemies on right (Pakistan)
      ctx.fillStyle = '#2f855a'; ctx.fillRect(0, groundY, CANVAS_W * 0.4, 40);
      ctx.fillStyle = '#718096'; ctx.fillRect(CANVAS_W * 0.6, groundY, CANVAS_W * 0.4, 40);
      // Border line
      ctx.strokeStyle = '#e53e3e'; ctx.lineWidth = 2; ctx.setLineDash([8, 4]);
      ctx.beginPath(); ctx.moveTo(CANVAS_W * 0.5, groundY); ctx.lineTo(CANVAS_W * 0.5, CANVAS_H); ctx.stroke();
      ctx.setLineDash([]);
      // Labels
      ctx.fillStyle = '#fff'; ctx.font = 'bold 11px Fredoka, sans-serif';
      ctx.fillText('INDIA', 20, groundY + 25);
      ctx.fillText('PAKISTAN', CANVAS_W - 80, groundY + 25);
      // Delhi marker
      ctx.fillStyle = '#FF9933'; ctx.beginPath(); ctx.arc(80, groundY + 15, 4, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.font = '9px sans-serif'; ctx.fillText('Delhi', 68, groundY + 10);
    } else {
      // Defence: Pakistan on left, India (Delhi) on right
      ctx.fillStyle = '#718096'; ctx.fillRect(0, groundY, CANVAS_W * 0.4, 40);
      ctx.fillStyle = '#2f855a'; ctx.fillRect(CANVAS_W * 0.6, groundY, CANVAS_W * 0.4, 40);
      ctx.strokeStyle = '#e53e3e'; ctx.lineWidth = 2; ctx.setLineDash([8, 4]);
      ctx.beginPath(); ctx.moveTo(CANVAS_W * 0.5, groundY); ctx.lineTo(CANVAS_W * 0.5, CANVAS_H); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#fff'; ctx.font = 'bold 11px Fredoka, sans-serif';
      ctx.fillText('PAKISTAN', 20, groundY + 25);
      ctx.fillText('INDIA', CANVAS_W - 60, groundY + 25);
      ctx.fillStyle = '#FF9933'; ctx.beginPath(); ctx.arc(CANVAS_W - 80, groundY + 15, 4, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.font = '9px sans-serif'; ctx.fillText('Delhi', CANVAS_W - 92, groundY + 10);
    }

    // Clouds
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    g.clouds.forEach((c) => {
      ctx.beginPath(); ctx.ellipse(c.x, c.y, c.w / 2, 12, 0, 0, Math.PI * 2); ctx.fill();
    });

    // Player jet
    const p = g.player;
    const facingRight = isAttack;
    drawJet(ctx, p.x, p.y, p.w, p.h, facingRight, true, g.frame);

    // Player bullets
    ctx.fillStyle = '#FFD700';
    g.bullets.forEach((b) => { ctx.fillRect(b.x, b.y, b.w, b.h); });

    // Enemy planes
    g.enemies.forEach((e) => {
      drawJet(ctx, e.x, e.y, e.w, e.h, e.dir > 0, false, g.frame);
    });

    // Enemy bullets
    ctx.fillStyle = '#FF4444';
    g.enemyBullets.forEach((b) => { ctx.fillRect(b.x, b.y, b.w, b.h); });

    // Explosions
    g.explosions.forEach((ex) => {
      const progress = ex.frame / ex.max;
      const radius = 8 + progress * 22;
      ctx.fillStyle = `rgba(255, ${Math.floor(165 * (1 - progress))}, 0, ${1 - progress})`;
      ctx.beginPath(); ctx.arc(ex.x + 20, ex.y + 10, radius, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 0, ${(1 - progress) * 0.5})`;
      ctx.beginPath(); ctx.arc(ex.x + 20, ex.y + 10, radius * 0.5, 0, Math.PI * 2); ctx.fill();
    });

    // HUD
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(0, 0, CANVAS_W, 34);
    ctx.fillStyle = '#FFF'; ctx.font = 'bold 13px Fredoka, sans-serif';
    ctx.fillText(`Score: ${scoreRef.current}`, 12, 22);
    ctx.fillText(`Kills: ${g.kills}/30`, 140, 22);
    const modeLabel = isAttack ? 'ATTACK MODE' : 'DEFENCE MODE';
    ctx.fillStyle = isAttack ? '#fc8181' : '#63b3ed';
    ctx.fillText(modeLabel, CANVAS_W / 2 - 45, 22);
    // Health bar
    const hbX = CANVAS_W - 160;
    const hbW = 140;
    const hbH = 12;
    const hbY = 11;
    ctx.fillStyle = '#333';
    ctx.fillRect(hbX, hbY, hbW, hbH);
    const hpPercent = livesRef.current / 20;
    ctx.fillStyle = hpPercent > 0.5 ? '#48bb78' : hpPercent > 0.25 ? '#ecc94b' : '#fc8181';
    ctx.fillRect(hbX, hbY, hbW * hpPercent, hbH);
    ctx.strokeStyle = '#555';
    ctx.strokeRect(hbX, hbY, hbW, hbH);
    ctx.fillStyle = '#fff'; ctx.font = 'bold 10px sans-serif';
    ctx.fillText(`HP: ${livesRef.current}/20`, hbX + 4, hbY + 10);
  };

  const drawJet = (ctx, x, y, w, h, facingRight, isPlayer, frame) => {
    const bodyColor = isPlayer ? '#2d6a4f' : '#4a5568';
    const noseColor = isPlayer ? '#1b4332' : '#2d3748';
    const wingTop = isPlayer ? '#FF9933' : '#666';
    const wingBot = isPlayer ? '#138808' : '#666';
    const tailColor = isPlayer ? '#FFFFFF' : '#555';
    const markColor = isPlayer ? null : '#c0392b';

    ctx.fillStyle = bodyColor;
    if (facingRight) {
      ctx.fillRect(x, y + 4, w - 10, h - 8);
      ctx.fillStyle = noseColor;
      ctx.beginPath(); ctx.moveTo(x + w - 10, y + 2); ctx.lineTo(x + w + 5, y + h / 2); ctx.lineTo(x + w - 10, y + h - 2); ctx.fill();
      ctx.fillStyle = wingTop; ctx.fillRect(x + 10, y - 5, 18, 5);
      ctx.fillStyle = wingBot; ctx.fillRect(x + 10, y + h, 18, 5);
      ctx.fillStyle = tailColor; ctx.fillRect(x, y, 7, h);
      if (isPlayer && frame % 4 < 2) { ctx.fillStyle = '#FFA500'; ctx.fillRect(x - 7, y + 8, 7, 8); }
    } else {
      ctx.fillRect(x + 10, y + 4, w - 10, h - 8);
      ctx.fillStyle = noseColor;
      ctx.beginPath(); ctx.moveTo(x + 10, y + 2); ctx.lineTo(x - 5, y + h / 2); ctx.lineTo(x + 10, y + h - 2); ctx.fill();
      ctx.fillStyle = wingTop; ctx.fillRect(x + 16, y - 5, 18, 5);
      ctx.fillStyle = wingBot; ctx.fillRect(x + 16, y + h, 18, 5);
      ctx.fillStyle = tailColor; ctx.fillRect(x + w - 7, y, 7, h);
      if (isPlayer && frame % 4 < 2) { ctx.fillStyle = '#FFA500'; ctx.fillRect(x + w, y + 8, 7, 8); }
    }
    if (markColor) {
      ctx.fillStyle = markColor;
      ctx.beginPath(); ctx.arc(x + w / 2, y + h / 2, 4, 0, Math.PI * 2); ctx.fill();
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center p-4">
      <div className="mb-3 flex items-center justify-between w-full max-w-[800px]">
        <h2 className="font-heading text-lg font-bold text-white">
          {isAttack ? 'Attack Mode' : 'Defence Mode'} — Delhi Air Base
        </h2>
        <button
          data-testid="air-combat-quit"
          onClick={() => navigate('/air-combat')}
          className="text-gray-500 hover:text-white text-sm font-bold transition-colors"
        >
          Quit
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        data-testid="air-combat-canvas"
        className="rounded-2xl border-2 border-gray-700 shadow-2xl"
        style={{ maxWidth: '100%', height: 'auto' }}
      />
      <div className="mt-3 flex gap-4 text-gray-500 font-body text-xs">
        <span>Arrow keys: Move</span>
        <span>Spacebar: Shoot</span>
        <span>Destroy 30 enemies to win!</span>
      </div>
    </div>
  );
}
